/**
 * The form view's paste field (Option C, C3; opened by the rail's or Add from…'s Paste connection string): paste, read
 * the preview, then **Apply** (or Enter). Replaces the apply-on-change `ConnectionStringPasteField`: nothing reaches the
 * form before Apply, and an unrecognised string leaves the form untouched. A string for another engine reads "Apply to
 * PostgreSQL" and switches through the draft contract (`applyPatch`: that engine's draft is restored or seeded, the one
 * on screen is kept). Escape or the close button closes the field (`ui:connection_paste_field_closed`). A string several
 * engines read alike, none of them this form's (ruling after C3 review r5), shows "Reads as: …"; Apply waits for a pick.
 * A pasted string collapses into a chip (final UX review UX-03): the input beside it is empty, so the string is never on
 * screen — nor in the input at all: the paste is taken from the clipboard event, the browser's insertion cancelled
 * (`takePaste`, uxfix review UXF-01), and so is a dropped text (`takeDrop`). Typing or pasting replaces it; Backspace /
 * Delete or the chip's ✕ clears it. The formats are not in a placeholder too long to show (UX-12): they are the input's description (its title) and the
 * unrecognised message's second line, so the hint stays one line; with a chip the input keeps a small basis, so the chip,
 * the input, Apply and ✕ stay on one row where the pane is wide enough (UX review r2, R-3).
 */
import { useEffect, useMemo, useRef } from 'react';
import { ClipboardPaste, X } from 'lucide-react';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import { Button } from '@/components/ui/button';
import { ReasonButton } from '@/components/ui/ReasonButton';
import { cn } from '@/lib/utils';
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { parseConnectionString, type ConnectionStringParseResult } from '../../services/connection-string/parseConnectionString';
import { PasteParseStatus } from './PasteParseStatus';
import { PasteReadsAs } from './PasteReadsAs';
import { CHOOSE_ENGINE_FIRST } from './PasteDetectionCard';
import { buildPastePreview, pasteChoiceMessage, pastedChipLabel } from './pastePreview';
import { PastedStringChip } from './PastedStringChip';
import { FUNNEL_VERSION } from '../hooks/useConnectionDialogFunnel';
import { usePasteTracking } from './usePasteTracking';
import { useReadsAs } from './useReadsAs';
import { takeDrop, takePaste, usePasteCaret, type TakenPaste } from './takePaste';

type Props = {
  /** The engine whose form is on screen. */
  engine: DatabaseEngine;
  /** The values a paste for `target` lands on (that engine's draft, its first-visit seed, or the live form). */
  baseValuesFor: (target: DatabaseEngine) => Partial<ConnectionFormValues>;
  /** The field's text, held by the dialog (`usePasteEntry`) so text waiting for Apply counts as input. */
  text: string;
  /** How it got there: a pasted string shows as a chip. */
  textVia: 'type' | 'paste';
  onTextChange: (text: string, via: 'type' | 'paste') => void;
  onApply: (result: ConnectionStringParseResult) => void;
  onClose: () => void;
  /** Moves each time the user asks for the field (its entry point): the input takes focus. 0: never asked. */
  focusRequest: number;
  track: TrackFunction;
};

export const PASTE_INPUT_ID = 'connection-string-paste';
const CHIP_ID = 'connection-string-paste-chip';
export const PASTE_FORMATS_HINT = 'Reads ADO.NET, Kusto, URL, JDBC, EZConnect and libpq strings, Azure Storage connection strings and URLs, and SQLite file paths.';

export function PastePaneField({ engine, baseValuesFor, text, textVia, onTextChange: setText, onApply, onClose, focusRequest, track }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const placeCaret = usePasteCaret(inputRef);
  const tracking = usePasteTracking('pane_field', track);
  // The form's engine settles a string whose format the rules cannot tell (C3 review r3, C3-13).
  const result = useMemo(() => (text.trim() ? parseConnectionString(text, { engine }) : null), [text, engine]);
  // Several engines read it alike and none is this form's: the picked engine's reading is the one shown and applied.
  const readsAs = useReadsAs(text, result, track);
  const shown = readsAs.result;
  const preview = shown ? buildPastePreview(shown, shown.engine ? baseValuesFor(shown.engine) : {}) : null;

  useEffect(() => {
    if (focusRequest > 0) inputRef.current?.focus();
  }, [focusRequest]);

  const apply = () => {
    if (!shown || !preview) return;
    // The parse event of what was just typed goes out before the apply (C3 review C3-07).
    tracking.flush();
    onApply(shown);
  };
  const applyLabel = preview && preview.engine !== engine ? `Apply to ${preview.engineLabel}` : 'Apply';
  const chip = textVia === 'paste' && text.trim() ? pastedChipLabel(preview, result?.format) : null;
  const change = (next: string, via: 'type' | 'paste') => {
    setText(next, via);
    tracking.observe(next, next.trim() ? parseConnectionString(next, { engine }) : null, via === 'paste');
  };
  // A paste, or a text dropped on the input, never enters it (UXF-01): the field takes it as a paste.
  const taken = (pasted: TakenPaste | null) => {
    if (!pasted) return;
    placeCaret(pasted);
    change(pasted.value, 'paste');
  };
  const clear = (via: 'button' | 'key') => {
    setText('', 'type');
    track('ui:connection_string_cleared', { source: 'pane_field', via, funnel_version: FUNNEL_VERSION });
    inputRef.current?.focus();
  };
  const unavailable = preview
    ? undefined
    : readsAs.choices ? CHOOSE_ENGINE_FIRST : result ? `Nothing to apply: ${result.refusal ? 'see why under the field' : 'the string was not recognized'}` : 'Paste a connection string first';

  return (
    <div className="mb-4 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3" data-testid="connection-string-paste-field">
      <label htmlFor={PASTE_INPUT_ID} className="mb-1 flex items-center gap-2 text-sm font-medium">
        <ClipboardPaste className="h-4 w-4 opacity-70" aria-hidden />
        Paste a connection string
      </label>
      <div className="flex flex-wrap items-center gap-2">
        {chip !== null && <PastedStringChip id={CHIP_ID} label={chip} onClear={() => clear('button')} />}
        <input
          ref={inputRef}
          id={PASTE_INPUT_ID}
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={chip !== null ? '' : 'ADO.NET, URL, JDBC, libpq, EZConnect…'}
          aria-describedby={chip !== null ? `${CHIP_ID} connection-string-paste-hint` : 'connection-string-paste-hint'}
          title={PASTE_FORMATS_HINT}
          className={cn(
            'block h-8 min-w-0 flex-1 rounded border border-input bg-background px-3 font-mono text-xs shadow-sm focus:border-primary focus:ring-primary',
            chip !== null ? 'basis-16' : 'basis-60',
          )}
          // The chip stands for a pasted string: the input shows none of it (the dialog keeps it for Apply).
          value={chip !== null ? '' : text}
          onPaste={(event) => taken(takePaste(event))}
          onDrop={(event) => taken(takeDrop(event))}
          onChange={(event) => change(event.target.value, 'type')}
          onKeyDown={(event) => {
            if (chip !== null && (event.key === 'Backspace' || event.key === 'Delete') && !event.nativeEvent.isComposing) {
              event.preventDefault();
              clear('key');
              return;
            }
            if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
            event.preventDefault();
            apply();
          }}
          data-testid="connection-string-paste-input"
        />
        <ReasonButton type="button" size="sm" reason={unavailable} onClick={apply} data-testid="connection-string-apply-button">
          {applyLabel}
        </ReasonButton>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Close the paste field" onClick={onClose} data-testid="connection-string-paste-close">
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <p id="connection-string-paste-hint" className="mt-1 text-xs text-muted-foreground">
        Nothing changes in the form until you apply. Enter applies, Esc closes.
      </p>
      {result && (
        <div className="mt-2 space-y-1.5">
          {readsAs.choices && <PasteReadsAs engines={readsAs.choices} picked={readsAs.picked} onPick={readsAs.choose} />}
          {readsAs.choices && !preview ? (
            <p className="text-xs text-muted-foreground" data-testid="connection-string-choose-engine">
              {pasteChoiceMessage(readsAs.choices)}
            </p>
          ) : (
            <PasteParseStatus preview={preview} failed={!preview} refusal={result} headline />
          )}
        </div>
      )}
    </div>
  );
}
